import { useEffect, useState, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useFocus,
  useInteractions,
  FloatingArrow,
} from '@floating-ui/react';
import './Tooltip.css';

const renderIcon = (iconType) => {
  switch (iconType) {
    case 'success':
      // Green checkmark circle
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="8.75" fill="#41D48C" />
          <path
            d="M6 10L8.5 12.5L14 7"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    
    case 'error':
      // Red triangle warning icon
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M10 3L2 17H18L10 3Z"
            fill="#F04D50"
            stroke="#F04D50"
            strokeWidth="0.5"
          />
          <text
            x="10"
            y="14"
            fontSize="10"
            fontWeight="700"
            fill="white"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
          >
            !
          </text>
        </svg>
      );
    
    case 'question':
      // Yellow question mark circle
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="8.75" fill="#EDBC0C" />
          <text
            x="10"
            y="14.5"
            fontSize="12"
            fontWeight="700"
            fill="white"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
          >
            ?
          </text>
        </svg>
      );
    
    case 'warning':
      // Gray triangle warning icon
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M10 3L2 17H18L10 3Z"
            fill="#CED4DA"
            stroke="#CED4DA"
            strokeWidth="0.5"
          />
          <text
            x="10"
            y="14"
            fontSize="10"
            fontWeight="700"
            fill="white"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
          >
            !
          </text>
        </svg>
      );
    
    case 'info':
    default:
      // Gray info circle (default)
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="8" fill="white" />
          <circle cx="10" cy="10" r="8.33" fill="#CED4DA" />
          <text
            x="10"
            y="14"
            fontSize="12"
            fontWeight="600"
            fill="#FFFFFF"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
          >
            i
          </text>
        </svg>
      );
  }
};

export const Tooltip = ({ 
  text, 
  position = 'top',
  iconType = 'info',
  showTooltip = true,
  className = '' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(
    () => window.matchMedia('(pointer: coarse)').matches
  );
  const arrowRef = useRef(null);

  const shouldShowTooltip = Boolean(showTooltip && text);
  const isOpen = shouldShowTooltip && isVisible;

  // Detect touch device and listen for changes (useful for DevTools emulation)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const handler = (e) => setIsTouchDevice(e.matches);
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const { refs, floatingStyles, context, placement } = useFloating({
    open: isOpen,
    onOpenChange: setIsVisible,
    placement: position,
    middleware: [
      offset(8),
      flip({ fallbackAxisSideDirection: 'start' }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Disable hover/focus on touch devices - only click/tap will work
  const hover = useHover(context, {
    enabled: shouldShowTooltip && !isTouchDevice,
  });

  const focus = useFocus(context, {
    enabled: shouldShowTooltip && !isTouchDevice,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
  ]);

  useEffect(() => {
    if (!shouldShowTooltip) {
      setIsVisible(false);
    }
  }, [shouldShowTooltip]);

  // Click/tap only works on touch devices
  const handleClick = () => {
    if (!shouldShowTooltip || !isTouchDevice) return;

    setIsVisible((prev) => !prev);
  };

  const containerClass = `tooltip-container ${
    shouldShowTooltip ? 'tooltip-container--with-tooltip' : ''
  } ${className}`.trim();

  // Determine animation class based on actual placement (after flip)
  const getAnimationClass = () => {
    const side = placement.split('-')[0];
    return `tooltip-popup--${side}`;
  };

  return (
    <div className={containerClass}>
      <button
        ref={refs.setReference}
        className="tooltip-icon"
        onClick={handleClick}
        aria-label={text || ''}
        type="button"
        {...getReferenceProps()}
      >
        {renderIcon(iconType)}
      </button>
      
      {isOpen && (
        <div 
          ref={refs.setFloating}
          className={`tooltip-popup ${getAnimationClass()}`}
          style={floatingStyles}
          role="tooltip"
          {...getFloatingProps()}
        >
          {text}
          <FloatingArrow
            ref={arrowRef}
            context={context}
            className="tooltip-floating-arrow"
            fill="#f2f2f2"
            width={12}
            height={6}
          />
        </div>
      )}
    </div>
  );
};
